import { useContext } from 'react'

import { useForm, useLatestEntity, useRealtimeSubscription } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignContributionActions from '@redux/modules/campaignContribution'

import defaultFormState, { requiredFields } from '@models/campaignContribution'

import PageContext from '@contexts/pageContext'
import RealtimeContext from '@contexts/realtimeContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CampaignContributionModel, CampaignContributionRequestOptions } from '@models/types'

import useCurrentUser from './useCurrentUser'

export const generateUrls = (campaignContribution?: Partial<CampaignContributionModel>) => {
  const { id } = campaignContribution || {}

  return {
    editCampaignContributionDetailsUrl: `#/systemManager/campaignContributions/${id}/edit/details`,
    editCampaignContributionUrl: `#/systemManager/campaignContributions/${id}/edit/`,
    campaignContributionsIndexUrl: '#/systemManager/campaignContributions',
  }
}

type AddCampaignContributionParams = {
  data: { payload: CampaignContributionModel },
  dispatch: AppDispatch,
}

// Add Campaign Contribution to Redux
const addCampaignContribution = (params: AddCampaignContributionParams) => {
  const { data: { payload }, dispatch } = params
  const { addCampaignContribution: addFn } = campaignContributionActions

  return dispatch(addFn({ campaignContribution: payload }))
}

type CreateCampaignContributionParams = {
  campaignContributionParams: Partial<CampaignContributionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignContributionRequestOptions,
}

const createCampaignContribution = (params: CreateCampaignContributionParams) => {
  const { dispatch, campaignContributionParams, requestOptions } = params
  const { createCampaignContribution: createFn } = campaignContributionActions

  return dispatch(createFn(campaignContributionParams, requestOptions))
}

type UpdateCampaignContributionParams = {
  campaignContribution: CampaignContributionModel,
  campaignContributionParams: Partial<CampaignContributionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignContributionRequestOptions,
}

const updateCampaignContribution = (params: UpdateCampaignContributionParams) => {
  const {
    dispatch, campaignContribution, campaignContributionParams, requestOptions,
  } = params
  const { updateCampaignContribution: updateFn } = campaignContributionActions

  const updatedParams = {
    id: campaignContribution.id,
    ...campaignContributionParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCampaignContributionParams = {
  campaignContribution: DeleteParams<CampaignContributionModel>,
  dispatch: AppDispatch,
}

const deleteCampaignContribution = (params: DeleteCampaignContributionParams) => {
  const { dispatch, campaignContribution } = params
  const { deleteCampaignContribution: deleteFn } = campaignContributionActions

  return dispatch(deleteFn(campaignContribution))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignContributionForm(
  campaignContribution: Partial<CampaignContributionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignContributionForm = useForm(
    defaultFormState,
    { entity: campaignContribution, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignContribution.id, campaignContribution.cache_key],
  )

  return {
    ...campaignContributionForm,
  }
}

export const useRelations = (campaignContribution: Partial<CampaignContributionModel> = {}) => {
  const { campaign_id, user_id } = campaignContribution

  const { campaigns, users } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const user = user_id && users[user_id] ? users[user_id] : {}

  return {
    campaign,
    user,
  }
}

type LatestEntityPayload = {
  entity: CampaignContributionModel,
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignContributionRequestOptions,
}

function useCampaignContribution(initEntity: Partial<CampaignContributionModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: campaignContribution }: LatestEntityPayload = useLatestEntity(initEntity, 'campaignContributions')
  const { id } = campaignContribution

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaign } = useRelations(campaignContribution)

  useReduxAction(
    'campaignContributions',
    'loadCampaignContribution',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignContributions)

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  const { currentUser } = useCurrentUser()

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: { payload: CampaignContributionModel }) => addCampaignContribution({ data, dispatch }),
    },
    channelName: 'campaignContributions',
    clientRef,
    eventName: 'campaignContribution.createdOrUpdated.complete',
  }, [currentUser.id])

  return {
    callbacks: {
      createCampaignContribution: (
        campaignContributionParams: Partial<CampaignContributionModel>,
        entityOptions?: CampaignContributionRequestOptions,
      ) => (
        createCampaignContribution({ campaignContributionParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCampaignContribution: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignContributionModal',
        payload: { campaignContribution },
      }),
      deleteCampaignContribution: () => deleteCampaignContribution({ dispatch, campaignContribution }),
      updateCampaignContribution: (
        campaignContributionParams: Partial<CampaignContributionModel>,
        entityOptions?: CampaignContributionRequestOptions,
      ) => (
        updateCampaignContribution({
          campaignContribution, campaignContributionParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    campaignContribution,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignContribution.id),
    updating,
    urls: generateUrls(campaignContribution),
  }
}

export default useCampaignContribution
